import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface FilterOption {
  code: string;
  label: string;
}

interface ChipAutocompleteProps {
  label: string;
  options?: FilterOption[];
  loadOptions?: (query: string) => Promise<FilterOption[]>;
  selected: string[];
  onChange: (codes: string[]) => void;
  loading?: boolean;
  placeholder?: string;
  helperText?: string;
  freeSolo?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const NO_OPTIONS: FilterOption[] = [];

const filter = createFilterOptions<FilterOption>({
  stringify: (option) => `${option.code} ${option.label}`,
});

function normalizeCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of codes) {
    const t = c.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(t);
  }
  return result;
}

function splitFreeText(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ChipAutocomplete({
  label,
  options,
  loadOptions,
  selected,
  onChange,
  loading,
  placeholder,
  helperText,
  freeSolo,
  disabled,
  sx,
}: ChipAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const [asyncOptions, setAsyncOptions] = useState<FilterOption[]>([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  // Tracks whether the current render cycle follows a chip selection so we
  // can distinguish MUI's "reset after selection" (wanted: clear input) from
  // "reset due to value-prop ref change" (unwanted: preserve input).
  const selectionJustMade = useRef(false);

  const controlsInput = Boolean(loadOptions || freeSolo);

  useEffect(() => {
    if (!loadOptions) return;
    // Only the latest input's answer lands, and a failed load stops the spinner.
    let cancelled = false;
    setAsyncLoading(true);
    const t = setTimeout(() => {
      loadOptions(inputValue)
        .then((results) => {
          if (!cancelled) setAsyncOptions(results);
        })
        .catch(() => {
          if (!cancelled) setAsyncOptions([]);
        })
        .finally(() => {
          if (!cancelled) setAsyncLoading(false);
        });
    }, 300);
    return () => {
      clearTimeout(t);
      cancelled = true;
    };
  }, [inputValue, loadOptions]);

  const baseOptions = loadOptions ? asyncOptions : (options ?? NO_OPTIONS);

  const selectedAsOptions = useMemo(
    () =>
      selected.map((code) => baseOptions.find((o) => o.code === code) ?? { code, label: code }),
    [baseOptions, selected],
  );

  const mergedOptions = useMemo(() => {
    const extra = selectedAsOptions.filter((s) => !baseOptions.some((o) => o.code === s.code));
    return [...extra, ...baseOptions];
  }, [baseOptions, selectedAsOptions]);

  const commitFreeText = useCallback(
    (raw: string) => {
      const parts = splitFreeText(raw);
      if (!parts.length) return;
      onChange(normalizeCodes([...selected, ...parts]));
      setInputValue("");
    },
    [onChange, selected],
  );

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      freeSolo={freeSolo}
      disabled={disabled}
      options={mergedOptions}
      value={selectedAsOptions}
      loading={loadOptions ? asyncLoading : loading}
      limitTags={2}
      filterOptions={loadOptions ? (x) => x : filter}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      isOptionEqualToValue={(option, value) => {
        const optCode = typeof option === "string" ? option : option.code;
        const valCode = typeof value === "string" ? value : value.code;
        return optCode === valCode;
      }}
      inputValue={controlsInput ? inputValue : undefined}
      onChange={(_, newValue) => {
        selectionJustMade.current = true;
        const codes = newValue.map((v) => (typeof v === "string" ? v : v.code));
        onChange(normalizeCodes(codes));
      }}
      onInputChange={
        controlsInput
          ? (_, v, reason) => {
              if (reason === "input") {
                setInputValue(v);
              } else if (reason === "reset" && selectionJustMade.current) {
                selectionJustMade.current = false;
                setInputValue("");
              }
            }
          : undefined
      }
      onBlur={() => {
        if (freeSolo && inputValue.trim()) {
          commitFreeText(inputValue);
        }
      }}
      renderValue={(value, getItemProps) =>
        value.map((option, index) => {
          const { key, ...rest } = getItemProps({ index });
          const label = typeof option === "string" ? option : option.label;
          return <Chip key={key} label={label} {...rest} />;
        })
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Typography variant="body2" component="span">
              {option.label}
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selected.length === 0 ? placeholder : undefined}
          helperText={helperText}
          slotProps={{
            ...params.slotProps,

            htmlInput: {
              ...params.slotProps.htmlInput,
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (freeSolo && inputValue.trim() && (e.key === "," || e.key === "Enter")) {
                  e.preventDefault();
                  e.stopPropagation();
                  commitFreeText(inputValue);
                } else {
                  params.slotProps.htmlInput.onKeyDown?.(e);
                }
              },
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                if (!freeSolo) return;
                const text = e.clipboardData.getData("text");
                if (text.includes(",") || text.includes("\n")) {
                  e.preventDefault();
                  commitFreeText(text);
                }
              },
            }
          }}
        />
      )}
      sx={sx}
    />
  );
}

export default ChipAutocomplete;
